import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { toast } from 'react-hot-toast';
import {
  Loader2, Search, MousePointerClick, Navigation, Monitor, Smartphone, Rocket,
  RotateCcw, CheckCircle2, AlertTriangle, FlaskConical, ExternalLink, LayoutGrid, ArrowUp,
  MapPin, Paintbrush, Megaphone, MessageSquare, ShoppingCart, Plus, Trash2, Eye, EyeOff,
  Target, Timer, Type,
} from 'lucide-react';

// On-site widget stüdyosu: markanın müşteri sitesine basılan tüm widget'ları tek çatı.
// Canlı sayfa iframe'de `?glowy_edit=1` ile açılır, taslak anında önizlenir.
// iframe içindeki taraf: backend/public/glowytics-widget-editor.js + glowytics-widgets.js
// Şimdilik sadece hosgelsin — backend'de de (pixel.js EDITOR_BRAND_IDS + EDITOR_EMAILS)
// aynı kilit var; burayı değiştirmek tek başına başka markayı açmaz.
const API = 'https://api.glowytics.com';
const BASE = `${API}/api/pixel/editor`;
const BRAND_ID = '8a07ce35-0fb3-4019-b0b2-60617b3c1d29'; // hosgelsin
const BRAND_NAME = 'hosgelsin';

const TYPE_META = {
  announcement: { label: 'Duyuru çubuğu', icon: Megaphone, hint: 'Sayfanın en üstünde/altında ince şerit' },
  popup: { label: 'Popup', icon: MessageSquare, hint: 'Modal — gecikmeli, kaydırmada ya da çıkış niyetinde' },
  recommendation: { label: 'Ürün önerisi', icon: LayoutGrid, hint: 'Ürün sayfasında öneri şeridi' },
  cart_upsell: { label: 'Sepet önerisi', icon: ShoppingCart, hint: 'Sepette öneri + tek tıkla sepete ekleme' },
};
const INLINE = ['recommendation', 'cart_upsell'];

const POSITIONS = [
  { key: 'before', label: 'Önüne' },
  { key: 'after', label: 'Arkasına' },
  { key: 'append', label: 'İçine (sona)' },
];

// Çalışma zamanının varsayılan görünümü. Kaydederken sadece bundan FARKLI alanlar yazılır;
// fark yoksa style = null (yani widget script'indeki varsayılan geçerli).
const DEFAULT_STYLE = {
  recommendation: {
    columns: 4, mobileColumns: 2, imageRatio: '1:1', radius: 12, border: true, shadow: false,
    titleSize: 20, titleAlign: 'left', showOldPrice: true, showButton: false, buttonText: 'İncele',
    textColor: '#1f2937', titleColor: '#1f2937', priceColor: '#111827', cardBg: '#ffffff',
    borderColor: '#e5e7eb', buttonBg: '#111827', buttonColor: '#ffffff', sectionBg: null,
  },
  announcement: { bg: '#111827', color: '#ffffff', linkColor: '#ffffff', fontSize: 14, align: 'center', bold: false },
  popup: { bg: '#ffffff', color: '#111827', buttonBg: '#111827', buttonColor: '#ffffff', radius: 16, width: 420, overlay: true, position: 'center' },
};
DEFAULT_STYLE.cart_upsell = DEFAULT_STYLE.recommendation;

function defaultStyleFor(type) { return { ...(DEFAULT_STYLE[type] || {}) }; }

function diffStyle(type, design) {
  const base = DEFAULT_STYLE[type] || {};
  const out = {};
  Object.keys(base).forEach((k) => {
    if (design[k] !== base[k] && design[k] !== null && design[k] !== undefined) out[k] = design[k];
  });
  // Başlık rengi verilmezse metin rengini izler; panelde görünenle aynı kalsın diye sabitle
  if (INLINE.includes(type) && out.textColor && !out.titleColor) out.titleColor = base.titleColor;
  return Object.keys(out).length ? out : null;
}

function withEditParam(url) {
  try {
    const u = new URL(url);
    u.searchParams.set('glowy_edit', '1');
    return u.toString();
  } catch (e) { return url; }
}

const emptyDraftKey = (d) => d.id || `draft-${d.type}`;

export default function WidgetStudio() {
  const [state, setState] = useState(null);        // { siteOrigin, pages, hasCatalog }
  const [widgets, setWidgets] = useState([]);
  const [types, setTypes] = useState([]);          // backend'den gelen tür + varsayılan taslak
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saved, setSaved] = useState(null);        // kaydedilmiş hali (kirli mi karşılaştırması)
  const [picker, setPicker] = useState(false);     // yeni widget tür seçici

  const [query, setQuery] = useState('');
  const [pageUrl, setPageUrl] = useState(null);
  const [pathInput, setPathInput] = useState('');
  const [frameReady, setFrameReady] = useState(false);
  const [framePath, setFramePath] = useState('');
  const [mode, setMode] = useState('browse');      // browse | pick
  const [device, setDevice] = useState('desktop');
  const [selLabel, setSelLabel] = useState('');
  const [matches, setMatches] = useState(null);
  const [rendered, setRendered] = useState(null);  // { present, placeholder }
  const [tab, setTab] = useState('content');
  const [testing, setTesting] = useState(false);
  const [testResults, setTestResults] = useState(null);
  const [saving, setSaving] = useState(false);

  const frameRef = useRef(null);
  const testFrameRef = useRef(null);
  const testWaiter = useRef(null);
  const lastPreviewKey = useRef(null);

  const siteOrigin = state?.siteOrigin || null;
  const type = draft?.type || null;
  const meta = type ? TYPE_META[type] : null;

  // ─── Yükleme ───
  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [sr, wr] = await Promise.all([
        fetch(`${BASE}/state?brandId=${BRAND_ID}`, { credentials: 'include' }),
        fetch(`${BASE}/widgets?brandId=${BRAND_ID}`, { credentials: 'include' }),
      ]);
      if (!sr.ok) throw new Error(sr.status === 403 ? 'Bu sayfaya erişimin yok' : `HTTP ${sr.status}`);
      if (!wr.ok) throw new Error(`HTTP ${wr.status}`);
      const s = await sr.json();
      const w = await wr.json();
      setState(s);
      setWidgets(w.widgets || []);
      setTypes(w.types || []);
      if (s.pages?.length) setPageUrl(prev => prev || s.pages[0].url);
      setSelectedId(prev => prev || (w.widgets?.[0]?.id ?? null));
    } catch (e) {
      toast.error(`Yüklenemedi: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Seçili widget değişince taslağı tazele
  useEffect(() => {
    if (!selectedId) return;
    const w = widgets.find(x => x.id === selectedId);
    if (!w) return;
    const d = normalize(w);
    setDraft(d);
    setSaved(d);
    setTab('content');
    setTestResults(null);
    setSelLabel('');
    setMatches(null);
  }, [selectedId, widgets]);

  function normalize(w) {
    return {
      id: w.id || null,
      type: w.type,
      name: w.name || TYPE_META[w.type]?.label || 'Widget',
      enabled: !!w.enabled,
      priority: w.priority || 0,
      targeting: { pathMode: 'all', paths: [], devices: ['desktop', 'mobile'], ...(w.targeting || {}) },
      placement: { selector: null, position: 'before', ...(w.placement || {}) },
      trigger: { on: 'load', frequency: 'always', delayMs: 4000, scrollPct: 50, ...(w.trigger || {}) },
      content: { ...(w.content || {}) },
      design: { ...defaultStyleFor(w.type), ...(w.style || {}) },
    };
  }

  const setDraftField = (path, value) => {
    setDraft(d => {
      if (!d) return d;
      const next = { ...d };
      if (path.includes('.')) {
        const [a, b] = path.split('.');
        next[a] = { ...next[a], [b]: value };
      } else next[path] = value;
      return next;
    });
  };
  const setD = (k, v) => setDraftField(`design.${k}`, v);

  // ─── iframe mesajları ───
  const post = useCallback((msg, frame = frameRef.current) => {
    if (!frame?.contentWindow || !siteOrigin) return;
    frame.contentWindow.postMessage(msg, siteOrigin);
  }, [siteOrigin]);

  useEffect(() => {
    if (!siteOrigin) return undefined;
    const onMsg = (e) => {
      if (e.origin !== siteOrigin) return;
      const m = e.data || {};
      if (typeof m.type !== 'string' || !m.type.startsWith('glowy:')) return;

      if (testFrameRef.current && e.source === testFrameRef.current.contentWindow) {
        if (testWaiter.current) testWaiter.current(m);
        return;
      }
      if (!frameRef.current || e.source !== frameRef.current.contentWindow) return;

      if (m.type === 'glowy:ready') {
        setFrameReady(true);
        setFramePath(m.path || '');
        setRendered(null);
        lastPreviewKey.current = null;
      } else if (m.type === 'glowy:rendered') {
        setRendered({ present: !!m.present, placeholder: !!m.placeholder });
      } else if (m.type === 'glowy:selected') {
        if (!m.selector) { toast.error('Bu öğe için sağlam bir seçici üretilemedi, başka bir öğe dene'); return; }
        setDraftField('placement.selector', m.selector);
        setSelLabel(m.label || '');
        setMatches(m.matches);
        setTestResults(null);
      } else if (m.type === 'glowy:navigating') {
        setFrameReady(false);
      }
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, [siteOrigin]);

  useEffect(() => {
    if (frameReady) post({ type: 'glowy:pick', on: mode === 'pick' });
  }, [mode, frameReady, post]);

  // Taslak değişince iframe'de yeniden çiz (yazarken beklemesin diye kısa gecikme)
  const previewPayload = useMemo(() => {
    if (!draft) return null;
    return {
      id: emptyDraftKey(draft),
      type: draft.type,
      targeting: draft.targeting,
      placement: INLINE.includes(draft.type) ? draft.placement : {},
      trigger: draft.trigger,
      content: draft.content,
      style: diffStyle(draft.type, draft.design),
    };
  }, [draft]);

  useEffect(() => {
    if (!frameReady || !previewPayload) return undefined;
    const key = previewPayload.id;
    if (lastPreviewKey.current && lastPreviewKey.current !== key) {
      post({ type: 'glowy:clear', key: lastPreviewKey.current });
    }
    lastPreviewKey.current = key;
    const t = setTimeout(() => post({ type: 'glowy:draft', widget: previewPayload, flash: false }), 260);
    return () => clearTimeout(t);
  }, [previewPayload, frameReady, post]);

  const openPage = (url) => {
    setFrameReady(false);
    setRendered(null);
    setPageUrl(url);
  };

  const openPath = () => {
    const p = pathInput.trim();
    if (!p || !siteOrigin) return;
    openPage(`${siteOrigin}${p.startsWith('/') ? p : `/${p}`}`);
  };

  // ─── Yeni widget ───
  const startNew = (t) => {
    const tpl = types.find(x => x.type === t)?.draft || { type: t, content: {}, targeting: {}, placement: {}, trigger: {} };
    const d = normalize({ ...tpl, id: null });
    setSelectedId(null);
    setDraft(d);
    setSaved(null);
    setTab('content');
    setPicker(false);
    setTestResults(null);
  };

  // ─── Kaydet / yayınla ───
  const save = async () => {
    if (!draft) return;
    const body = {
      brandId: BRAND_ID,
      type: draft.type,
      name: draft.name,
      enabled: draft.enabled,
      priority: draft.priority,
      targeting: draft.targeting,
      placement: INLINE.includes(draft.type) ? draft.placement : {},
      trigger: draft.trigger,
      content: draft.content,
      style: diffStyle(draft.type, draft.design),
    };
    if (draft.enabled) {
      // eslint-disable-next-line no-alert
      if (!window.confirm(`"${draft.name}" ${BRAND_NAME} CANLI sitesinde yayına girecek. Emin misin?`)) return;
    }
    setSaving(true);
    try {
      const url = draft.id ? `${BASE}/widgets/${draft.id}?brandId=${BRAND_ID}` : `${BASE}/widgets?brandId=${BRAND_ID}`;
      const r = await fetch(url, {
        method: draft.id ? 'PUT' : 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
      const w = d.widget;
      setWidgets(list => (draft.id ? list.map(x => (x.id === w.id ? { ...w, stats: x.stats } : x)) : [...list, { ...w, stats: { shown: 0, clicked: 0 } }]));
      setSelectedId(w.id);
      setSaved(normalize(w));
      toast.success(draft.enabled
        ? 'Yayınlandı. Ziyaretçilere 1 saat içinde yansır (script önbelleği).'
        : 'Kaydedildi (kapalı — ziyaretçiye gösterilmiyor).');
    } catch (e) {
      toast.error(`Kaydedilemedi: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const toggleEnabled = async (w) => {
    const next = !w.enabled;
    // eslint-disable-next-line no-alert
    if (next && !window.confirm(`"${w.name}" ${BRAND_NAME} CANLI sitesinde yayına girecek. Emin misin?`)) return;
    try {
      const r = await fetch(`${BASE}/widgets/${w.id}?brandId=${BRAND_ID}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          brandId: BRAND_ID, type: w.type, name: w.name, enabled: next, priority: w.priority,
          targeting: w.targeting, placement: w.placement, trigger: w.trigger, content: w.content, style: w.style,
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
      setWidgets(list => list.map(x => (x.id === w.id ? { ...d.widget, stats: x.stats } : x)));
      if (selectedId === w.id) { const n = normalize(d.widget); setDraft(n); setSaved(n); }
      toast.success(next ? 'Yayında' : 'Durduruldu');
    } catch (e) {
      toast.error(`Değiştirilemedi: ${e.message}`);
    }
  };

  const remove = async (w) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`"${w.name}" silinsin mi? Geri alınamaz.`)) return;
    try {
      const r = await fetch(`${BASE}/widgets/${w.id}?brandId=${BRAND_ID}`, { method: 'DELETE', credentials: 'include' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setWidgets(list => list.filter(x => x.id !== w.id));
      if (selectedId === w.id) { setSelectedId(null); setDraft(null); setSaved(null); }
      post({ type: 'glowy:clear', key: w.id });
      toast.success('Silindi');
    } catch (e) {
      toast.error(`Silinemedi: ${e.message}`);
    }
  };

  // ─── Seçici testi (inline türler) ───
  const runTest = async () => {
    const sel = (draft?.placement?.selector || '').trim();
    if (!sel || !state?.pages?.length) return;
    const others = state.pages.filter(p => p.url !== pageUrl);
    const sample = [...others].sort(() => Math.random() - 0.5).slice(0, 5);
    setTesting(true);
    setTestResults([]);
    const results = [];
    for (const p of sample) {
      // eslint-disable-next-line no-await-in-loop
      const res = await new Promise((resolve) => {
        const timer = setTimeout(() => { testWaiter.current = null; resolve({ ...p, status: 'timeout' }); }, 20000);
        testWaiter.current = (m) => {
          if (m.type === 'glowy:ready') {
            testFrameRef.current.contentWindow.postMessage({ type: 'glowy:check', selector: sel }, siteOrigin);
          } else if (m.type === 'glowy:checked') {
            clearTimeout(timer);
            testWaiter.current = null;
            resolve({ ...p, status: m.count === 1 ? 'ok' : m.count === 0 ? 'missing' : 'multi', count: m.count });
          }
        };
        testFrameRef.current.src = withEditParam(p.url);
      });
      results.push(res);
      setTestResults([...results]);
    }
    if (testFrameRef.current) testFrameRef.current.src = 'about:blank';
    setTesting(false);
  };

  const filteredPages = useMemo(() => {
    const list = state?.pages || [];
    const q = query.trim().toLocaleLowerCase('tr');
    return (q ? list.filter(p => p.title.toLocaleLowerCase('tr').includes(q)) : list).slice(0, 60);
  }, [state, query]);

  const dirty = useMemo(() => {
    if (!draft) return false;
    if (!saved) return true;
    return JSON.stringify(draft) !== JSON.stringify(saved);
  }, [draft, saved]);

  const tabs = useMemo(() => {
    if (!type) return [];
    const out = [['content', 'İçerik', Type]];
    if (INLINE.includes(type)) out.push(['place', 'Yerleşim', MapPin]);
    if (!INLINE.includes(type)) out.push(['trigger', 'Tetikleyici', Timer]);
    out.push(['target', 'Hedefleme', Target]);
    out.push(['design', 'Tasarım', Paintbrush]);
    return out;
  }, [type]);

  if (loading) {
    return <div className="flex items-center justify-center h-[60vh] text-gray-500"><Loader2 className="w-5 h-5 animate-spin mr-2" />Yükleniyor…</div>;
  }
  if (!state) return <div className="p-8 text-gray-600">Stüdyo açılamadı.</div>;

  return (
    <div className="flex flex-col h-[calc(100vh-64px)] bg-gray-50">
      {/* Başlık */}
      <div className="flex items-center justify-between px-6 py-3 bg-white border-b border-gray-200">
        <div className="flex items-center gap-3">
          <LayoutGrid className="w-5 h-5 text-indigo-600" />
          <div>
            <h1 className="text-[15px] font-semibold text-gray-900">Widgets</h1>
            <p className="text-xs text-gray-500">{BRAND_NAME} · canlı sitede duyuru, popup, öneri ve sepet widget'ları</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-lg border border-gray-200 p-0.5 bg-gray-50">
            <button type="button" onClick={() => setMode('browse')}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md ${mode === 'browse' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500'}`}>
              <Navigation className="w-3.5 h-3.5" />Gezin
            </button>
            <button type="button" onClick={() => setMode('pick')} disabled={!frameReady || !INLINE.includes(type)}
              title={INLINE.includes(type) ? '' : 'Bu widget türü sabit konumlu, seçici gerekmiyor'}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md disabled:opacity-40 ${mode === 'pick' ? 'bg-indigo-600 text-white shadow-sm' : 'text-gray-500'}`}>
              <MousePointerClick className="w-3.5 h-3.5" />Yer seç
            </button>
          </div>
          <div className="flex rounded-lg border border-gray-200 p-0.5 bg-gray-50">
            <button type="button" onClick={() => setDevice('desktop')} title="Masaüstü"
              className={`p-1.5 rounded-md ${device === 'desktop' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-400'}`}><Monitor className="w-4 h-4" /></button>
            <button type="button" onClick={() => setDevice('mobile')} title="Mobil"
              className={`p-1.5 rounded-md ${device === 'mobile' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-400'}`}><Smartphone className="w-4 h-4" /></button>
          </div>
        </div>
      </div>

      <div className="flex flex-1 min-h-0">
        {/* Widget listesi */}
        <aside className="w-[236px] shrink-0 bg-white border-r border-gray-200 overflow-y-auto">
          <div className="p-3 border-b border-gray-100">
            <button type="button" onClick={() => setPicker(p => !p)}
              className="w-full flex items-center justify-center gap-1.5 py-2 text-sm font-medium rounded-lg bg-indigo-600 text-white hover:bg-indigo-700">
              <Plus className="w-4 h-4" />Yeni widget
            </button>
            {picker && (
              <div className="mt-2 space-y-1">
                {Object.entries(TYPE_META).map(([t, m]) => (
                  <button type="button" key={t} onClick={() => startNew(t)}
                    className="w-full flex items-start gap-2 p-2 text-left rounded-lg border border-gray-200 hover:border-indigo-300 hover:bg-indigo-50/40">
                    <m.icon className="w-4 h-4 text-indigo-600 mt-0.5 shrink-0" />
                    <span>
                      <span className="block text-xs font-medium text-gray-900">{m.label}</span>
                      <span className="block text-[10px] text-gray-500 leading-tight">{m.hint}</span>
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="py-2">
            {widgets.length === 0 && !draft && (
              <p className="px-3 py-6 text-xs text-gray-400 text-center">Henüz widget yok</p>
            )}
            {widgets.map(w => {
              const M = TYPE_META[w.type] || TYPE_META.announcement;
              const active = selectedId === w.id;
              return (
                <div key={w.id}
                  className={`group px-3 py-2 border-l-2 cursor-pointer ${active ? 'border-indigo-600 bg-indigo-50/60' : 'border-transparent hover:bg-gray-50'}`}
                  onClick={() => setSelectedId(w.id)}>
                  <div className="flex items-center gap-2">
                    <M.icon className={`w-3.5 h-3.5 shrink-0 ${w.enabled ? 'text-green-600' : 'text-gray-400'}`} />
                    <span className="flex-1 truncate text-xs font-medium text-gray-900">{w.name}</span>
                    <button type="button" title={w.enabled ? 'Yayında — durdur' : 'Kapalı — yayınla'}
                      onClick={(e) => { e.stopPropagation(); toggleEnabled(w); }}
                      className={`shrink-0 p-1 rounded ${w.enabled ? 'text-green-600 hover:bg-green-50' : 'text-gray-400 hover:bg-gray-100'}`}>
                      {w.enabled ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                    </button>
                    <button type="button" title="Sil" onClick={(e) => { e.stopPropagation(); remove(w); }}
                      className="shrink-0 p-1 rounded text-gray-300 hover:text-red-600 hover:bg-red-50 opacity-0 group-hover:opacity-100">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <div className="mt-0.5 pl-5 flex items-center gap-2 text-[10px] text-gray-500">
                    <span>{M.label}</span>
                    {w.stats && (w.stats.shown > 0 || w.stats.clicked > 0) && (
                      <span className="ml-auto">{w.stats.shown} gösterim · {w.stats.clicked} tık</span>
                    )}
                  </div>
                </div>
              );
            })}
            {draft && !draft.id && (
              <div className="px-3 py-2 border-l-2 border-indigo-600 bg-indigo-50/60">
                <div className="flex items-center gap-2">
                  {meta && <meta.icon className="w-3.5 h-3.5 text-indigo-600 shrink-0" />}
                  <span className="flex-1 truncate text-xs font-medium text-gray-900">{draft.name}</span>
                  <span className="text-[10px] text-indigo-700">yeni</span>
                </div>
              </div>
            )}
          </div>

          {/* Sayfa seçimi */}
          <section className="p-3 border-t border-gray-100">
            <h2 className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-2">Önizleme sayfası</h2>
            <div className="relative mb-1.5">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-gray-400" />
              <input value={query} onChange={e => setQuery(e.target.value)} placeholder={`${state.pages.length} üründe ara`}
                className="w-full pl-7 pr-2 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-200" />
            </div>
            <div className="max-h-40 overflow-y-auto rounded-lg border border-gray-100">
              {filteredPages.map(p => (
                <button type="button" key={p.url} onClick={() => openPage(p.url)}
                  className={`w-full flex items-center gap-2 px-2 py-1.5 text-left text-[11px] hover:bg-gray-50 ${p.url === pageUrl ? 'bg-indigo-50 text-indigo-700' : 'text-gray-700'}`}>
                  {p.image ? <img src={p.image} alt="" className="w-6 h-6 rounded object-cover shrink-0" /> : <div className="w-6 h-6 rounded bg-gray-100 shrink-0" />}
                  <span className="truncate">{p.title}</span>
                </button>
              ))}
              {filteredPages.length === 0 && <div className="px-3 py-3 text-[11px] text-gray-400">Ürün bulunamadı</div>}
            </div>
            <div className="mt-1.5 flex gap-1">
              <input value={pathInput} onChange={e => setPathInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') openPath(); }}
                placeholder="/sepet/ gibi bir yol"
                className="flex-1 px-2 py-1.5 text-[11px] font-mono border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-200" />
              <button type="button" onClick={openPath} className="px-2 py-1.5 text-[11px] rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50">Aç</button>
            </div>
            {framePath && (
              <a href={`${siteOrigin}${framePath}`} target="_blank" rel="noreferrer" className="mt-1.5 inline-flex items-center gap-1 text-[10px] text-gray-500 hover:text-gray-700">
                <ExternalLink className="w-3 h-3" />{framePath}
              </a>
            )}
          </section>
        </aside>

        {/* Widget ayarları */}
        <aside className="w-[326px] shrink-0 bg-white border-r border-gray-200 overflow-y-auto">
          {!draft ? (
            <div className="p-6 text-sm text-gray-500">Soldan bir widget seç ya da yeni ekle.</div>
          ) : (
            <>
              <section className="p-4 border-b border-gray-100">
                <div className="flex items-center gap-2 mb-2">
                  {meta && <meta.icon className="w-4 h-4 text-indigo-600" />}
                  <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">{meta?.label}</span>
                </div>
                <input value={draft.name} onChange={e => setDraftField('name', e.target.value.slice(0, 60))}
                  placeholder="Widget adı (panelde görünür)"
                  className="w-full px-2.5 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-200" />
                <label className="flex items-center justify-between mt-2.5">
                  <span className="text-xs text-gray-600">Canlıda yayında</span>
                  <Toggle value={draft.enabled} onChange={v => setDraftField('enabled', v)} />
                </label>
                {rendered && (
                  <p className={`mt-2 text-[11px] ${rendered.placeholder ? 'text-amber-700' : 'text-green-700'}`}>
                    {rendered.placeholder ? 'Bu sayfada içerik yok — yer tutucu gösteriliyor' : 'Önizlemede basıldı'}
                  </p>
                )}
              </section>

              <div className="flex border-b border-gray-200 px-3 pt-2.5 gap-3 overflow-x-auto">
                {tabs.map(([k, label, Icon]) => (
                  <button type="button" key={k} onClick={() => { setTab(k); if (k !== 'place') setMode('browse'); }}
                    className={`flex items-center gap-1 pb-2 text-xs font-medium border-b-2 -mb-px whitespace-nowrap ${tab === k ? 'border-indigo-600 text-indigo-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
                    <Icon className="w-3.5 h-3.5" />{label}
                  </button>
                ))}
              </div>

              {tab === 'content' && <ContentPanel draft={draft} set={setDraftField} />}
              {tab === 'place' && (
                <PlacePanel draft={draft} set={setDraftField} selLabel={selLabel} matches={matches}
                  frameReady={frameReady} mode={mode} post={post} testing={testing} testResults={testResults}
                  runTest={runTest} />
              )}
              {tab === 'trigger' && <TriggerPanel draft={draft} set={setDraftField} />}
              {tab === 'target' && <TargetPanel draft={draft} set={setDraftField} framePath={framePath} />}
              {tab === 'design' && (
                <DesignPanel type={draft.type} design={draft.design} setD={setD}
                  limit={draft.content.limit} onReset={() => setDraftField('design', defaultStyleFor(draft.type))} />
              )}

              <section className="p-4">
                {dirty && <p className="text-[11px] text-indigo-700 mb-2">Kaydedilmemiş değişiklik var</p>}
                <button type="button" onClick={save} disabled={saving || !dirty}
                  className="w-full flex items-center justify-center gap-1.5 py-2 text-sm font-semibold rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />}
                  {draft.enabled ? 'Yayınla' : 'Kaydet (kapalı)'}
                </button>
                {saved && (
                  <button type="button" onClick={() => setDraft(saved)} disabled={!dirty}
                    className="mt-2 w-full flex items-center justify-center gap-1.5 py-1.5 text-xs text-gray-500 hover:text-gray-700 disabled:opacity-40">
                    <RotateCcw className="w-3.5 h-3.5" />Değişiklikleri geri al
                  </button>
                )}
              </section>
            </>
          )}
        </aside>

        {/* Canlı sayfa */}
        <main className="flex-1 min-w-0 flex items-stretch justify-center p-4 overflow-auto">
          {pageUrl ? (
            <div className={`relative bg-white shadow-sm border border-gray-200 rounded-lg overflow-hidden ${device === 'mobile' ? 'w-[390px]' : 'w-full'}`}>
              {!frameReady && (
                <div className="absolute inset-0 flex items-center justify-center bg-white/70 z-10 text-sm text-gray-500">
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />Sayfa yükleniyor…
                </div>
              )}
              <iframe ref={frameRef} key={pageUrl} src={withEditParam(pageUrl)} title="Canlı önizleme" className="w-full h-full border-0" />
            </div>
          ) : (
            <div className="self-center text-sm text-gray-500">Soldan bir sayfa seç</div>
          )}
        </main>
      </div>

      <iframe ref={testFrameRef} title="seçici testi" className="absolute w-[1280px] h-[800px] -left-[9999px] top-0 opacity-0 pointer-events-none" src="about:blank" />
    </div>
  );
}

// ─── Ortak küçük bileşenler ───
function Row({ label, children }) {
  return (
    <div className="flex items-center justify-between gap-3 py-1.5">
      <span className="text-xs text-gray-600">{label}</span>
      <div className="flex items-center gap-1.5">{children}</div>
    </div>
  );
}

function Seg({ value, options, onChange }) {
  return (
    <div className="flex rounded-md border border-gray-200 p-0.5 bg-gray-50">
      {options.map(([v, l]) => (
        <button type="button" key={String(v)} onClick={() => onChange(v)}
          className={`px-2 py-0.5 text-[11px] rounded ${value === v ? 'bg-white shadow-sm text-gray-900 font-medium' : 'text-gray-500'}`}>
          {l}
        </button>
      ))}
    </div>
  );
}

function Color({ value, onChange }) {
  return (
    <label className="flex items-center gap-1.5 cursor-pointer">
      <span className="font-mono text-[10px] text-gray-500 uppercase">{value}</span>
      <input type="color" value={value || '#ffffff'} onChange={e => onChange(e.target.value)}
        className="w-7 h-7 p-0 border border-gray-200 rounded cursor-pointer bg-transparent" />
    </label>
  );
}

function Toggle({ value, onChange }) {
  return (
    <button type="button" onClick={() => onChange(!value)}
      className={`relative w-9 h-5 rounded-full transition-colors ${value ? 'bg-indigo-600' : 'bg-gray-300'}`}>
      <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all ${value ? 'left-[18px]' : 'left-0.5'}`} />
    </button>
  );
}

function Group({ title, children }) {
  return (
    <section className="px-4 py-3 border-b border-gray-100">
      {title && <h3 className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1">{title}</h3>}
      {children}
    </section>
  );
}

function Field({ label, value, onChange, placeholder, max = 160, mono = false }) {
  return (
    <label className="block mb-2">
      <span className="block text-[11px] text-gray-500 mb-1">{label}</span>
      <input value={value || ''} onChange={e => onChange(e.target.value.slice(0, max))} placeholder={placeholder}
        className={`w-full px-2.5 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-200 ${mono ? 'font-mono text-[11px]' : ''}`} />
    </label>
  );
}

// ─── İçerik sekmesi (türe göre) ───
function ContentPanel({ draft, set }) {
  const c = draft.content;
  const setC = (k, v) => set(`content.${k}`, v);
  if (draft.type === 'announcement') {
    return (
      <Group title="Duyuru">
        <Field label="Metin" value={c.text} onChange={v => setC('text', v)} placeholder="Ücretsiz kargo fırsatı" max={160} />
        <Field label="Buton metni (isteğe bağlı)" value={c.linkText} onChange={v => setC('linkText', v)} placeholder="Hemen bak" max={30} />
        <Field label="Buton linki" value={c.linkUrl} onChange={v => setC('linkUrl', v)} placeholder="/kampanya/ veya https://…" max={500} mono />
        <Row label="Konum"><Seg value={c.bar || 'top'} options={[['top', 'Üst'], ['bottom', 'Alt']]} onChange={v => setC('bar', v)} /></Row>
        <Row label="Kaydırınca sabit kalsın"><Toggle value={!!c.sticky} onChange={v => setC('sticky', v)} /></Row>
        <Row label="Kapatılabilir"><Toggle value={c.dismissible !== false} onChange={v => setC('dismissible', v)} /></Row>
      </Group>
    );
  }
  if (draft.type === 'popup') {
    return (
      <Group title="Popup">
        <Field label="Başlık" value={c.title} onChange={v => setC('title', v)} placeholder="Merhaba!" max={80} />
        <label className="block mb-2">
          <span className="block text-[11px] text-gray-500 mb-1">Metin</span>
          <textarea value={c.text || ''} onChange={e => setC('text', e.target.value.slice(0, 300))} rows={3}
            className="w-full px-2.5 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-200" />
        </label>
        <Field label="Görsel linki (isteğe bağlı)" value={c.imageUrl} onChange={v => setC('imageUrl', v)} placeholder="https://…" max={500} mono />
        <Field label="Buton metni" value={c.buttonText} onChange={v => setC('buttonText', v)} placeholder="İncele" max={30} />
        <Field label="Buton linki" value={c.buttonUrl} onChange={v => setC('buttonUrl', v)} placeholder="/kampanya/" max={500} mono />
      </Group>
    );
  }
  // recommendation + cart_upsell
  return (
    <Group title="İçerik">
      <Field label="Başlık" value={c.title} onChange={v => setC('title', v)} placeholder="Bunlar da ilgini çekebilir" max={80} />
      <Row label="Ürün sayısı">
        <Seg value={c.limit || 4} options={[[3, '3'], [4, '4'], [6, '6'], [8, '8']]} onChange={v => setC('limit', v)} />
      </Row>
      {draft.type === 'cart_upsell' && (<>
        <Row label="Tek tıkla sepete ekle"><Toggle value={c.addToCart !== false} onChange={v => setC('addToCart', v)} /></Row>
        {c.addToCart !== false && <Field label="Buton metni" value={c.buttonText} onChange={v => setC('buttonText', v)} placeholder="Sepete ekle" max={30} />}
        <p className="text-[11px] text-gray-500 mt-1">
          Varyasyonlu ürün ya da stok hatasında müşteri ürün sayfasına yönlendirilir.
        </p>
      </>)}
    </Group>
  );
}

// ─── Yerleşim sekmesi ───
function PlacePanel({ draft, set, selLabel, matches, frameReady, mode, post, testing, testResults, runTest }) {
  const sel = draft.placement.selector || '';
  return (
    <>
      <Group title="Yerleşim">
        {mode !== 'pick' && !sel && (
          <p className="text-xs text-gray-500 mb-2">Üstten <b>Yer seç</b>'e basıp sayfada bir bölüme tıkla. Boş bırakırsan footer'dan hemen önce çıkar.</p>
        )}
        {mode === 'pick' && <p className="text-xs text-indigo-700 mb-2">Sayfada widget'ın yanına geleceği bölüme tıkla.</p>}
        {selLabel && (
          <div className="flex items-center justify-between gap-2 text-[11px] text-gray-500 mb-1">
            <span className="truncate">Seçilen: <span className="font-mono text-gray-700">{selLabel}</span></span>
            <button type="button" onClick={() => post({ type: 'glowy:parent' })} disabled={!frameReady}
              title="Tıklama en içteki öğeyi yakalar; bölümün tamamı için bir üst öğeye çık"
              className="shrink-0 flex items-center gap-0.5 px-1.5 py-0.5 rounded border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40">
              <ArrowUp className="w-3 h-3" />Üst öğe
            </button>
          </div>
        )}
        <textarea value={sel} onChange={e => set('placement.selector', e.target.value || null)} rows={3}
          placeholder="CSS seçici (boş = footer'dan önce)"
          className="w-full px-2.5 py-2 text-[11px] font-mono border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-200" />
        {matches !== null && matches !== 1 && (
          <p className="text-[11px] text-amber-700 mt-1">Bu seçici sayfada {matches} öğe buluyor; ilki kullanılır.</p>
        )}
        <div className="grid grid-cols-3 gap-1 mt-2">
          {POSITIONS.map(p => (
            <button type="button" key={p.key} onClick={() => set('placement.position', p.key)}
              className={`py-1.5 text-xs rounded-md border ${draft.placement.position === p.key ? 'border-indigo-600 bg-indigo-600 text-white' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
              {p.label}
            </button>
          ))}
        </div>
        {sel && (
          <button type="button" onClick={() => { set('placement.selector', null); set('placement.position', 'before'); }}
            className="mt-2 w-full flex items-center justify-center gap-1.5 py-1.5 text-[11px] text-gray-500 hover:text-gray-700">
            <RotateCcw className="w-3 h-3" />Varsayılana al
          </button>
        )}
      </Group>

      <Group title="Diğer sayfalarda test">
        <p className="text-[11px] text-gray-500 mb-2">Seçici bir sayfada tutup diğerinde tutmayabilir. Rastgele 5 ürün sayfasında dener.</p>
        <button type="button" onClick={runTest} disabled={testing || !sel}
          className="w-full flex items-center justify-center gap-1.5 py-2 text-sm font-medium rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-40">
          {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <FlaskConical className="w-4 h-4" />}
          {testing ? 'Test ediliyor…' : 'Test et'}
        </button>
        {testResults && testResults.length > 0 && (
          <ul className="mt-2 space-y-1">
            {testResults.map(t => (
              <li key={t.url} className="flex items-center gap-1.5 text-[11px]">
                {t.status === 'ok' ? <CheckCircle2 className="w-3.5 h-3.5 text-green-600 shrink-0" /> : <AlertTriangle className="w-3.5 h-3.5 text-amber-600 shrink-0" />}
                <span className="truncate text-gray-700">{t.title}</span>
                <span className="ml-auto shrink-0 text-gray-500">
                  {t.status === 'ok' ? 'tuttu' : t.status === 'missing' ? 'bulunamadı' : t.status === 'multi' ? `${t.count} eşleşme` : 'zaman aşımı'}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Group>
    </>
  );
}

// ─── Tetikleyici sekmesi ───
function TriggerPanel({ draft, set }) {
  const t = draft.trigger;
  return (
    <Group title="Ne zaman çıksın">
      <Row label="Tetikleyici">
        <Seg value={t.on || 'load'} options={[['load', 'Hemen'], ['delay', 'Gecikmeli'], ['scroll', 'Kaydırmada'], ['exit', 'Çıkışta']]}
          onChange={v => set('trigger.on', v)} />
      </Row>
      {t.on === 'delay' && (
        <Row label={`Gecikme · ${Math.round((t.delayMs ?? 4000) / 1000)} sn`}>
          <input type="range" min={0} max={30000} step={1000} value={t.delayMs ?? 4000}
            onChange={e => set('trigger.delayMs', Number(e.target.value))} className="w-28 accent-indigo-600" />
        </Row>
      )}
      {t.on === 'scroll' && (
        <Row label={`Sayfanın %${t.scrollPct ?? 50}'sinde`}>
          <input type="range" min={10} max={100} step={5} value={t.scrollPct ?? 50}
            onChange={e => set('trigger.scrollPct', Number(e.target.value))} className="w-28 accent-indigo-600" />
        </Row>
      )}
      {t.on === 'exit' && <p className="text-[11px] text-gray-500 mb-1">Mobilde çıkış niyeti algılanamaz; orada 25 saniye sonra gösterilir.</p>}
      <Row label="Sıklık">
        <Seg value={t.frequency || 'session'}
          options={[['always', 'Her sayfada'], ['session', 'Oturumda 1'], ['day', 'Günde 1'], ['once', 'Bir kez']]}
          onChange={v => set('trigger.frequency', v)} />
      </Row>
      <p className="text-[11px] text-gray-500 mt-1">Sıklık ziyaretçinin tarayıcısında tutulur; editörde önizleme her zaman gösterilir.</p>
    </Group>
  );
}

// ─── Hedefleme sekmesi ───
function TargetPanel({ draft, set, framePath }) {
  const t = draft.targeting;
  const paths = t.paths || [];
  const setPaths = (list) => set('targeting.paths', list);
  const devices = t.devices || ['desktop', 'mobile'];
  const toggleDevice = (d) => {
    const next = devices.includes(d) ? devices.filter(x => x !== d) : [...devices, d];
    set('targeting.devices', next.length ? next : [d]);
  };
  return (
    <Group title="Nerede görünsün">
      <Row label="Sayfa kuralı">
        <Seg value={t.pathMode || 'all'} options={[['all', 'Tümü'], ['prefix', 'Başlayan'], ['contains', 'İçeren'], ['exact', 'Tam']]}
          onChange={v => set('targeting.pathMode', v)} />
      </Row>
      {t.pathMode !== 'all' && (
        <div className="mt-1.5 space-y-1">
          {paths.map((p, i) => (
            <div key={i} className="flex gap-1">
              <input value={p} onChange={e => setPaths(paths.map((x, j) => (j === i ? e.target.value : x)))}
                placeholder="/urun/"
                className="flex-1 px-2 py-1.5 text-[11px] font-mono border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-200" />
              <button type="button" onClick={() => setPaths(paths.filter((_, j) => j !== i))}
                className="px-2 rounded-lg border border-gray-200 text-gray-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          ))}
          <div className="flex gap-1">
            <button type="button" onClick={() => setPaths([...paths, ''])}
              className="flex-1 py-1.5 text-[11px] rounded-lg border border-dashed border-gray-300 text-gray-500 hover:bg-gray-50">+ Yol ekle</button>
            {framePath && !paths.includes(framePath) && (
              <button type="button" onClick={() => setPaths([...paths, framePath])}
                className="px-2 py-1.5 text-[11px] rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50">Açık sayfayı ekle</button>
            )}
          </div>
        </div>
      )}
      <Row label="Cihaz">
        <div className="flex gap-1">
          <button type="button" onClick={() => toggleDevice('desktop')}
            className={`px-2 py-1 rounded-md border text-[11px] ${devices.includes('desktop') ? 'border-indigo-600 bg-indigo-50 text-indigo-700' : 'border-gray-200 text-gray-500'}`}>Masaüstü</button>
          <button type="button" onClick={() => toggleDevice('mobile')}
            className={`px-2 py-1 rounded-md border text-[11px] ${devices.includes('mobile') ? 'border-indigo-600 bg-indigo-50 text-indigo-700' : 'border-gray-200 text-gray-500'}`}>Mobil</button>
        </div>
      </Row>
    </Group>
  );
}

// ─── Tasarım sekmesi (türe göre) ───
function DesignPanel({ type, design, setD, limit, onReset }) {
  if (type === 'announcement') {
    return (
      <>
        <Group title="Çubuk">
          <Row label="Zemin"><Color value={design.bg} onChange={v => setD('bg', v)} /></Row>
          <Row label="Yazı"><Color value={design.color} onChange={v => setD('color', v)} /></Row>
          <Row label="Buton rengi"><Color value={design.linkColor} onChange={v => setD('linkColor', v)} /></Row>
          <Row label={`Yazı boyutu · ${design.fontSize}px`}>
            <input type="range" min={11} max={22} value={design.fontSize} onChange={e => setD('fontSize', Number(e.target.value))} className="w-24 accent-indigo-600" />
          </Row>
          <Row label="Hizalama"><Seg value={design.align} options={[['left', 'Sol'], ['center', 'Orta']]} onChange={v => setD('align', v)} /></Row>
          <Row label="Kalın yazı"><Toggle value={design.bold} onChange={v => setD('bold', v)} /></Row>
        </Group>
        <ResetRow onReset={onReset} />
      </>
    );
  }
  if (type === 'popup') {
    return (
      <>
        <Group title="Kutu">
          <Row label="Zemin"><Color value={design.bg} onChange={v => setD('bg', v)} /></Row>
          <Row label="Yazı"><Color value={design.color} onChange={v => setD('color', v)} /></Row>
          <Row label={`Genişlik · ${design.width}px`}>
            <input type="range" min={280} max={720} step={10} value={design.width} onChange={e => setD('width', Number(e.target.value))} className="w-24 accent-indigo-600" />
          </Row>
          <Row label={`Köşe · ${design.radius}px`}>
            <input type="range" min={0} max={32} value={design.radius} onChange={e => setD('radius', Number(e.target.value))} className="w-24 accent-indigo-600" />
          </Row>
          <Row label="Konum">
            <Seg value={design.position} options={[['center', 'Orta'], ['bottom-right', 'Sağ alt'], ['bottom-left', 'Sol alt']]} onChange={v => setD('position', v)} />
          </Row>
          <Row label="Arka planı karart"><Toggle value={design.overlay} onChange={v => setD('overlay', v)} /></Row>
        </Group>
        <Group title="Buton">
          <Row label="Buton rengi"><Color value={design.buttonBg} onChange={v => setD('buttonBg', v)} /></Row>
          <Row label="Buton yazı rengi"><Color value={design.buttonColor} onChange={v => setD('buttonColor', v)} /></Row>
        </Group>
        <ResetRow onReset={onReset} />
      </>
    );
  }
  // ürün ızgarası (öneri + sepet)
  return (
    <>
      <Group title="Düzen">
        <Row label="Sütun (masaüstü)">
          <Seg value={design.columns} options={[[2, '2'], [3, '3'], [4, '4'], [5, '5'], [6, '6']]} onChange={v => setD('columns', v)} />
        </Row>
        {limit % design.columns !== 0 && (
          <p className="text-[11px] text-amber-700 -mt-0.5 mb-1">{limit} ürün {design.columns} sütuna bölünmüyor; son satır yarım kalır.</p>
        )}
        <Row label="Sütun (mobil)">
          <Seg value={design.mobileColumns} options={[[1, '1'], [2, '2'], [3, '3']]} onChange={v => setD('mobileColumns', v)} />
        </Row>
        <Row label="Görsel oranı">
          <Seg value={design.imageRatio} options={[['1:1', '1:1'], ['4:5', '4:5'], ['3:4', '3:4'], ['2:3', '2:3']]} onChange={v => setD('imageRatio', v)} />
        </Row>
        <Row label="Eski fiyatı göster"><Toggle value={design.showOldPrice} onChange={v => setD('showOldPrice', v)} /></Row>
      </Group>

      <Group title="Başlık">
        <Row label="Hizalama"><Seg value={design.titleAlign} options={[['left', 'Sol'], ['center', 'Orta']]} onChange={v => setD('titleAlign', v)} /></Row>
        <Row label={`Boyut · ${design.titleSize}px`}>
          <input type="range" min={14} max={36} value={design.titleSize} onChange={e => setD('titleSize', Number(e.target.value))} className="w-24 accent-indigo-600" />
        </Row>
        <Row label="Renk"><Color value={design.titleColor} onChange={v => setD('titleColor', v)} /></Row>
      </Group>

      <Group title="Kart">
        <Row label={`Köşe · ${design.radius}px`}>
          <input type="range" min={0} max={24} value={design.radius} onChange={e => setD('radius', Number(e.target.value))} className="w-24 accent-indigo-600" />
        </Row>
        <Row label="Çerçeve"><Toggle value={design.border} onChange={v => setD('border', v)} /></Row>
        {design.border && <Row label="Çerçeve rengi"><Color value={design.borderColor} onChange={v => setD('borderColor', v)} /></Row>}
        <Row label="Gölge"><Toggle value={design.shadow} onChange={v => setD('shadow', v)} /></Row>
        <Row label="Kart zemini"><Color value={design.cardBg} onChange={v => setD('cardBg', v)} /></Row>
        <Row label="Ürün adı rengi"><Color value={design.textColor} onChange={v => setD('textColor', v)} /></Row>
        <Row label="Fiyat rengi"><Color value={design.priceColor} onChange={v => setD('priceColor', v)} /></Row>
        <Row label="Bölüm zemini">
          <Toggle value={design.sectionBg !== null} onChange={v => setD('sectionBg', v ? '#f9fafb' : null)} />
          {design.sectionBg !== null && <Color value={design.sectionBg} onChange={v => setD('sectionBg', v)} />}
        </Row>
      </Group>

      <Group title="Kart butonu">
        <Row label="Kartta buton göster"><Toggle value={design.showButton} onChange={v => setD('showButton', v)} /></Row>
        {design.showButton && (<>
          <input value={design.buttonText} onChange={e => setD('buttonText', e.target.value.slice(0, 30))} placeholder="Buton metni"
            className="w-full mt-1 mb-1 px-2.5 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-200" />
          <Row label="Buton rengi"><Color value={design.buttonBg} onChange={v => setD('buttonBg', v)} /></Row>
          <Row label="Buton yazı rengi"><Color value={design.buttonColor} onChange={v => setD('buttonColor', v)} /></Row>
        </>)}
      </Group>
      <ResetRow onReset={onReset} />
    </>
  );
}

function ResetRow({ onReset }) {
  return (
    <div className="px-4 py-2 border-b border-gray-100">
      <button type="button" onClick={onReset} className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-700">
        <RotateCcw className="w-3.5 h-3.5" />Tasarımı varsayılana al
      </button>
    </div>
  );
}
